import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bot, MessageSquare, Loader2, ChevronDown, Sparkles } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  isLoadingHistory?: boolean;
  onScrollToBottom?: () => void;
  quickQuestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
}

/**
 * MessageList - Virtualized message list with scroll-to-bottom detection
 * - Detects if user is at bottom
 * - Shows "New messages" pill when scrolled up
 * - Smooth scroll to bottom on new assistant messages
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  isLoadingHistory = false,
  onScrollToBottom,
  quickQuestions = [],
  onSuggestionClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const lastMessageIdRef = useRef<string | null>(null);

  // Check if user is at bottom of scroll
  const checkScrollPosition = useCallback(() => {
    // Find the scrollable parent (main element from ChatLayout)
    const scrollContainer = containerRef.current?.closest('main');
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const threshold = 100; // pixels from bottom
    const atBottom = scrollHeight - scrollTop - clientHeight < threshold;
    
    setIsAtBottom(atBottom);
    setShowScrollButton(!atBottom && messages.length > 0);
  }, [messages.length]);

  // Scroll to bottom smoothly
  const scrollToBottom = useCallback((force = false) => {
    const scrollContainer = containerRef.current?.closest('main');
    if (!scrollContainer || !messagesEndRef.current) return;
    
    if (force || isAtBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isAtBottom]);

  // Auto-scroll on new assistant messages if at bottom
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;

    const isNewMessage = lastMessage.id !== lastMessageIdRef.current;
    const isAssistantMessage = lastMessage.role === 'assistant';

    if (isNewMessage && isAssistantMessage && isAtBottom) {
      setTimeout(() => scrollToBottom(true), 100);
    }

    lastMessageIdRef.current = lastMessage.id;
  }, [messages, isAtBottom, scrollToBottom]);

  // Scroll to bottom when loading completes
  useEffect(() => {
    if (!isLoadingHistory && messages.length > 0 && isAtBottom) {
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [isLoadingHistory, messages.length, isAtBottom, scrollToBottom]);

  // Attach scroll listener
  useEffect(() => {
    // Find the scrollable parent (main element from ChatLayout)
    const scrollContainer = containerRef.current?.closest('main');
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', checkScrollPosition);
    checkScrollPosition(); // Initial check

    return () => {
      scrollContainer.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  if (isLoadingHistory) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--primary))] mx-auto mb-2" />
          <p className="text-[rgb(var(--muted))]">Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} className="w-full">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <EmptyState quickQuestions={quickQuestions} onSuggestionClick={onSuggestionClick} />
          </div>
        ) : (
          <div className="space-y-6 pb-4">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9c68f2] to-[#422ca5] flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-[rgb(var(--card))] rounded-2xl px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[rgb(var(--primary))]" />
                      <span className="text-[rgb(var(--muted))]">Thinking...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} className="h-4" />
          </div>
        )}
      </div>

      {/* Scroll to bottom button - fixed position above composer */}
      {showScrollButton && (
        <div 
          className="fixed z-50 right-4 sm:right-8 lg:right-[calc(256px+1rem)]"
          style={{
            bottom: '140px',
          }}
        >
          <button
            onClick={() => {
              scrollToBottom(true);
              onScrollToBottom?.();
            }}
            className="p-3 bg-[rgb(var(--primary))] text-[rgb(var(--primary-fg))] rounded-full shadow-lg hover:shadow-xl transition-all min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Scroll to bottom"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
};

const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex space-x-3 max-w-[85%] sm:max-w-[75%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
        {message.role === 'assistant' && (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9c68f2] to-[#422ca5] flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-[rgb(var(--fg))]" />
          </div>
        )}
        {message.role === 'user' && (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-4 h-4 text-[rgb(var(--fg))]" />
          </div>
        )}
        <div className={`rounded-2xl px-4 py-3 ${
          message.role === 'user'
            ? 'bg-blue-600 text-[rgb(var(--fg))]'
            : 'bg-[rgb(var(--card))] text-[rgb(var(--fg))]'
        }`}>
          <p className="whitespace-pre-wrap leading-relaxed text-sm sm:text-base">{message.content}</p>
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ quickQuestions?: string[]; onSuggestionClick?: (suggestion: string) => void }> = ({ 
  quickQuestions = [],
  onSuggestionClick 
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-8 sm:py-12">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#9c68f2] to-[#422ca5] rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
        <Bot className="w-8 h-8 sm:w-10 sm:h-10 text-[rgb(var(--fg))]" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold text-[rgb(var(--fg))] mb-2 text-center">
        How can I help you today?
      </h2>
      <p className="text-sm sm:text-base text-[rgb(var(--muted))] mb-6 sm:mb-8 text-center max-w-md">
        Ask me anything about Fiverflow, your workflow, or get tips to optimize your freelance business.
      </p>
      {quickQuestions.length > 0 && onSuggestionClick && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
          {quickQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => onSuggestionClick(question)}
              className="p-3 sm:p-4 text-left bg-[rgb(var(--card))] hover:bg-[rgb(var(--card))]/80 border border-[rgb(var(--border))] rounded-xl transition-all hover:border-[rgb(var(--primary))] group min-h-[44px]"
            >
              <div className="flex items-start space-x-2 sm:space-x-3">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[rgb(var(--primary))] mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-xs sm:text-sm text-[rgb(var(--fg))] group-hover:text-[rgb(var(--primary))] transition-colors">
                  {question}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

